import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "/logo-kairos.png";
const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Token de recuperação inválido');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      return;
    }

    setIsLoading(true);

    try {
      // Use XMLHttpRequest to avoid body stream issues
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/auth/admin/reset-password`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              reject(new Error(data.detail || 'Token inválido ou expirado'));
            }
          } catch {
            reject(new Error('Erro ao processar resposta'));
          }
        };
        xhr.onerror = () => reject(new Error('Erro de conexão'));
        xhr.send(JSON.stringify({ token, new_password: password }));
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/login');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Erro ao redefinir senha');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-kairos-orange/15 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-kairos-orange/10 rounded-full blur-[100px]"></div>
      </div>

      <div className="relative z-10 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-block mb-6">
            <img src={LOGO_URL} alt="Kairós" className="w-20 h-20 rounded-full object-cover mx-auto shadow-lg shadow-kairos-orange/20" />
          </div>
          <h1 className="font-display text-3xl font-bold text-white mb-2">Kairós</h1>
          <p className="text-gray-400">Encontro de Casais</p>
        </div>

        {/* Card */}
        <div className="bg-[var(--ds-surface)] border border-[var(--ds-border)] rounded-2xl p-8">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="font-display text-2xl text-white mb-4">Senha Alterada!</h2>
              <p className="text-gray-400 mb-4">
                Sua senha foi redefinida com sucesso.
              </p>
              <p className="text-gray-500 text-sm">
                Redirecionando para o login...
              </p>
            </div>
          ) : !token ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="font-display text-2xl text-white mb-4">Link Inválido</h2>
              <p className="text-gray-400 mb-8">
                O link de recuperação é inválido ou expirou.
              </p>
              <Link to="/admin/forgot-password">
                <Button className="w-full h-12 bg-gradient-to-r from-kairos-orange to-kairos-orange-light text-white font-semibold rounded-lg">
                  Solicitar Novo Link
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-display text-2xl text-white text-center mb-2">Nova Senha</h2>
              <p className="text-gray-400 text-center mb-8 text-sm">
                Digite sua nova senha
              </p>

              {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Nova Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      required
                      minLength={8}
                      className="w-full h-12 pl-12 pr-12 bg-[var(--ds-background)] border border-[var(--ds-border)] rounded-lg text-white placeholder:text-gray-600 focus:outline-none focus:border-kairos-orange/50 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Confirmar Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Digite novamente"
                      required
                      className="w-full h-12 pl-12 pr-12 bg-[var(--ds-background)] border border-[var(--ds-border)] rounded-lg text-white placeholder:text-gray-600 focus:outline-none focus:border-kairos-orange/50 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 bg-gradient-to-r from-kairos-orange to-kairos-orange-light text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(255,107,0,0.3)] transition-all disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    'Redefinir Senha'
                  )}
                </Button>
              </form>

              <Link 
                to="/admin/login" 
                className="flex items-center justify-center gap-2 text-gray-400 hover:text-white mt-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar ao login
              </Link>
            </>
          )}
        </div>

        {/* Créditos */}
        <div className="mt-12 pt-6 border-t border-white/10 text-center">
          <p className="text-gray-500 text-xs">
            Desenvolvido por{' '}
            <a 
              href="https://grooverdigital.com.br" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-kairos-orange hover:text-kairos-orange-light transition-colors font-medium"
            >
              Groover Digital e Tecnologia
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
